import React, { useEffect, useState, useMemo } from 'react';
import { transactionApi, accountApi, configApi } from '../services/api';

const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n: number) => n.toLocaleString('en-US');

/* ---------- Interfaces ---------- */
interface Transaction {
  id: number;
  account: string;
  ticker: string;
  tranType: number;
  tdate: string;
  shares: number;
  price: number;
  amount: number | null;
  commision: number;
  reasoning: string | null;
  notes: string | null;
  closed: boolean;
}
interface Account { id: number; account: string; }

interface CapItem {
  companyName: string; ticker: string; exchange: string;
  shares: number; cost: number; dividend: number;
  realizedGain: number; currentPrice: number;
  unrealized: number; totalValue: number;
}
interface CapPortfolio {
  portfolioName: string; items: CapItem[];
  totalCost: number; totalDividend: number;
  totalRealizedGain: number; totalUnrealized: number;
}

/* ---------- Transaction type constants ---------- */
const TT_BUY = 0, TT_SELL = 1, TT_DIV = 2, TT_SPLIT = 3, TT_RSPLIT = 4, TT_XOUT = 5, TT_XIN = 6, TT_ROC = 7;
const TRAN_TYPES: { [key: number]: string } = {
  [TT_BUY]: 'Buy', [TT_SELL]: 'Sell', [TT_DIV]: 'Dividend', [TT_SPLIT]: 'Split', [TT_RSPLIT]: 'Rev Split', [TT_XOUT]: 'Xfer Out', [TT_XIN]: 'Xfer In', [TT_ROC]: 'ROC',
};

const emptyForm = {
  account: 'JointPrimary', ticker: '', tranType: 0, tdate: new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })).toISOString().slice(0, 10),
  shares: 0, price: 0, commision: 0, reasoning: '', notes: '',
};

type TabKey = 'transactions' | 'allTransactions' | 'addTransaction' | 'capReturn';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'transactions', label: 'Stock Transactions' },
  { key: 'allTransactions', label: 'All Transactions' },
  { key: 'addTransaction', label: 'Add New Transaction' },
  { key: 'capReturn', label: 'Capital Return' },
];

/* ---------- Sub-components ---------- */
type TxnSortKey = 'tdate' | 'account' | 'ticker' | 'tranType' | 'shares' | 'price' | 'amount' | 'commision' | 'reasoning' | 'closed';

const TXN_COLUMNS: { key: TxnSortKey | 'actions'; label: string }[] = [
  { key: 'tdate', label: 'Date' },
  { key: 'account', label: 'Account' },
  { key: 'ticker', label: 'Ticker' },
  { key: 'tranType', label: 'Type' },
  { key: 'shares', label: 'Shares' },
  { key: 'price', label: 'Price' },
  { key: 'amount', label: 'Amount' },
  { key: 'commision', label: 'Commission' },
  { key: 'reasoning', label: 'Reasoning' },
  { key: 'closed', label: 'Closed' },
  { key: 'actions', label: 'Actions' },
];

const TransactionsTab: React.FC<{ showAll: boolean }> = ({ showAll }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [filterAccount, setFilterAccount] = useState('');
  const [filterTicker, setFilterTicker] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState<TxnSortKey>('tdate');
  const [sortAsc, setSortAsc] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<any>(null);
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());
  const [showColPicker, setShowColPicker] = useState(false);

  const toggleCol = (key: string) => {
    setHiddenCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const isVisible = (key: string) => !hiddenCols.has(key);

  useEffect(() => {
    accountApi.list().then((res) => {
      const sorted = [...res.data].sort((a: any, b: any) =>
        a.account === 'JointPrimary' ? -1 : b.account === 'JointPrimary' ? 1 : a.account.localeCompare(b.account)
      );
      setAccounts(sorted);
    }).catch(() => {});
  }, []);

  const loadData = () => {
    setLoading(true);
    const params: any = { show_all: showAll };
    if (filterAccount) params.account = filterAccount;
    if (filterTicker) params.ticker = filterTicker;
    transactionApi.list(params)
      .then((res) => setTransactions(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, [showAll, filterAccount]);

  const handleSearch = () => { loadData(); };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await transactionApi.create(form);
      setShowForm(false);
      setForm(emptyForm);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create transaction');
    }
  };
  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this transaction?')) return;
    await transactionApi.delete(id);
    loadData();
  };
  const handleEdit = (t: Transaction) => {
    setEditId(t.id);
    setEditForm({ account: t.account, ticker: t.ticker, tranType: t.tranType, tdate: t.tdate, shares: t.shares, price: t.price, amount: t.amount, commision: t.commision, reasoning: t.reasoning || '', notes: t.notes || '', closed: t.closed });
  };
  const handleEditSave = async () => {
    if (editId == null || !editForm) return;
    try { const { amount, ...payload } = editForm; await transactionApi.update(editId, payload); setEditId(null); setEditForm(null); loadData(); } catch { alert('Failed to save'); }
  };
  const handleEditCancel = () => { setEditId(null); setEditForm(null); };

  const handleSort = (key: TxnSortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(key === 'tdate' || key === 'account' || key === 'ticker' || key === 'reasoning'); }
  };
  const sortArrow = (key: TxnSortKey) => sortKey === key ? (sortAsc ? ' \u25B2' : ' \u25BC') : '';

  const sorted = useMemo(() => {
    const arr = [...transactions];
    const dir = sortAsc ? 1 : -1;
    arr.sort((a, b) => {
      let av: any, bv: any;
      if (sortKey === 'amount') {
        av = a.amount ?? a.shares * a.price; bv = b.amount ?? b.shares * b.price;
      } else {
        av = (a as any)[sortKey] ?? ''; bv = (b as any)[sortKey] ?? '';
      }
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
    return arr;
  }, [transactions, sortKey, sortAsc]);

  const thSort = (key: TxnSortKey, label: string) => (
    <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(key)}>{label}{sortArrow(key)}</th>
  );

  return (
    <>
      <div style={styles.toolbar}>
        <div style={styles.filters}>
          <label>Account: </label>
          <select value={filterAccount} onChange={(e) => setFilterAccount(e.target.value)} style={styles.select}>
            <option value="">All</option>
            {accounts.map((a) => <option key={a.id} value={a.account}>{a.account}</option>)}
          </select>
          <label style={{ marginLeft: '1em' }}>Ticker: </label>
          <input value={filterTicker} onChange={(e) => setFilterTicker(e.target.value)}
            style={{ ...styles.input, width: '8em' }} placeholder="e.g. AAPL" />
          <input type="button" value="Search" onClick={handleSearch} style={{ ...styles.button, marginLeft: '0.5em' }} />
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <input type="button" value="Columns" onClick={() => setShowColPicker(!showColPicker)} style={styles.button} />
            {showColPicker && (
              <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 10, backgroundColor: '#fff', border: '1px solid #0d58e6', padding: '6px 10px', fontSize: '0.8em', whiteSpace: 'nowrap', boxShadow: '2px 2px 6px rgba(0,0,0,0.15)' }}>
                {TXN_COLUMNS.map(c => (
                  <label key={c.key} style={{ display: 'block', cursor: 'pointer', padding: '2px 0' }}>
                    <input type="checkbox" checked={isVisible(c.key)} onChange={() => toggleCol(c.key)} /> {c.label}
                  </label>
                ))}
              </div>
            )}
          </div>
          <input type="button" value="New Transaction" onClick={() => { setShowForm(true); setForm({ ...emptyForm, tdate: new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })).toISOString().slice(0, 10) }); setError(''); }} style={styles.button} />
        </div>
      </div>

      {showForm && (
        <div style={styles.formWrapper}>
          <fieldset style={styles.fieldset}>
            <legend style={styles.legend}>New Transaction</legend>
            {error && <div style={styles.errorMsg}>{error}</div>}
            <form onSubmit={handleSubmit}>
              <ol style={styles.fieldList}>
                <li style={styles.field}>
                  <label style={styles.formLabel}>Account:</label>
                  <select value={form.account} onChange={(e) => setForm({ ...form, account: e.target.value })} style={styles.select} required autoComplete="off">
                    {accounts.map((a) => <option key={a.id} value={a.account}>{a.account}</option>)}
                  </select>
                </li>
                <li style={styles.field}>
                  <label style={styles.formLabel}>Ticker:</label>
                  <input value={form.ticker} onChange={(e) => setForm({ ...form, ticker: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '10em' }} required />
                </li>
                <li style={styles.field}>
                  <label style={styles.formLabel}>Type:</label>
                  <select value={form.tranType} onChange={(e) => setForm({ ...form, tranType: parseInt(e.target.value) })} style={styles.select}>
                    {Object.entries(TRAN_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </li>
                <li style={styles.field}>
                  <label style={styles.formLabel}>Date:</label>
                  <input type="date" value={form.tdate} onChange={(e) => setForm({ ...form, tdate: e.target.value })} style={styles.input} required />
                </li>
                <li style={styles.field}>
                  <label style={styles.formLabel}>Shares:</label>
                  <input type="number" value={form.shares} onChange={(e) => setForm({ ...form, shares: parseInt(e.target.value) || 0 })} style={{ ...styles.input, width: '8em' }} required />
                </li>
                <li style={styles.field}>
                  <label style={styles.formLabel}>Price:</label>
                  <input type="number" step="0.001" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} style={{ ...styles.input, width: '10em' }} required />
                </li>
                <li style={styles.field}>
                  <label style={styles.formLabel}>Commission:</label>
                  <input type="number" step="0.01" value={form.commision} onChange={(e) => setForm({ ...form, commision: parseFloat(e.target.value) || 0 })} style={{ ...styles.input, width: '8em' }} />
                </li>
                <li style={styles.field}>
                  <label style={styles.formLabel}>Reasoning:</label>
                  <input value={form.reasoning} onChange={(e) => setForm({ ...form, reasoning: e.target.value })} style={{ ...styles.input, width: '20em' }} />
                </li>
                <li style={styles.field}>
                  <label style={styles.formLabel}>Notes:</label>
                  <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ ...styles.input, width: '20em' }} />
                </li>
              </ol>
              <fieldset style={{ border: 'none', textAlign: 'right', padding: '0.3em 1em 1em' }}>
                <input type="submit" value="Create" style={styles.button} />
                <input type="button" value="Cancel" onClick={() => setShowForm(false)} style={{ ...styles.button, marginLeft: '8px' }} />
              </fieldset>
            </form>
          </fieldset>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              {isVisible('tdate') && thSort('tdate', 'Date')}
              {isVisible('account') && thSort('account', 'Account')}
              {isVisible('ticker') && thSort('ticker', 'Ticker')}
              {isVisible('tranType') && thSort('tranType', 'Type')}
              {isVisible('shares') && thSort('shares', 'Shares')}
              {isVisible('price') && thSort('price', 'Price')}
              {isVisible('amount') && thSort('amount', 'Amount')}
              {isVisible('commision') && thSort('commision', 'Commission')}
              {isVisible('reasoning') && thSort('reasoning', 'Reasoning')}
              {isVisible('closed') && thSort('closed', 'Closed')}
              {isVisible('actions') && <th style={styles.th}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sorted.map((t, idx) => {
              const rowBg = idx % 2 === 0 ? '#fff' : '#e6f2ff';
              const isEditing = editId === t.id;
              if (isEditing && editForm) {
                return (
                  <tr key={t.id} style={{ backgroundColor: '#ffffdd' }}>
                    {isVisible('tdate') && <td style={styles.td}><input type="date" value={editForm.tdate} onChange={(e) => setEditForm({ ...editForm, tdate: e.target.value })} style={{ ...styles.input, width: '9em' }} /></td>}
                    {isVisible('account') && <td style={styles.td}>
                      <select value={editForm.account} onChange={(e) => setEditForm({ ...editForm, account: e.target.value })} style={styles.select}>
                        {accounts.map((a) => <option key={a.id} value={a.account}>{a.account}</option>)}
                      </select>
                    </td>}
                    {isVisible('ticker') && <td style={styles.td}><input value={editForm.ticker} onChange={(e) => setEditForm({ ...editForm, ticker: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '6em' }} /></td>}
                    {isVisible('tranType') && <td style={styles.td}>
                      <select value={editForm.tranType} onChange={(e) => setEditForm({ ...editForm, tranType: parseInt(e.target.value) })} style={styles.select}>
                        {Object.entries(TRAN_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </td>}
                    {isVisible('shares') && <td style={styles.td}><input type="number" value={editForm.shares} onChange={(e) => { const s = parseInt(e.target.value) || 0; setEditForm({ ...editForm, shares: s, amount: s * editForm.price }); }} style={{ ...styles.input, width: '6em' }} /></td>}
                    {isVisible('price') && <td style={styles.td}><input type="number" step="0.001" value={editForm.price} onChange={(e) => { const p = parseFloat(e.target.value) || 0; setEditForm({ ...editForm, price: p, amount: editForm.shares * p }); }} style={{ ...styles.input, width: '7em' }} /></td>}
                    {isVisible('amount') && <td style={styles.td}><input type="number" step="0.01" value={parseFloat(((editForm.amount != null ? editForm.amount : editForm.shares * editForm.price) + editForm.commision).toFixed(2))} onChange={(e) => setEditForm({ ...editForm, amount: parseFloat(e.target.value) || 0 })} style={{ ...styles.input, width: '8em' }} /></td>}
                    {isVisible('commision') && <td style={styles.td}><input type="number" step="0.01" value={editForm.commision} onChange={(e) => setEditForm({ ...editForm, commision: parseFloat(e.target.value) || 0 })} style={{ ...styles.input, width: '6em' }} /></td>}
                    {isVisible('reasoning') && <td style={styles.td}><input value={editForm.reasoning} onChange={(e) => setEditForm({ ...editForm, reasoning: e.target.value })} style={{ ...styles.input, width: '12em' }} /></td>}
                    {isVisible('closed') && <td style={{ ...styles.td, textAlign: 'center' }}>
                      <input type="checkbox" checked={editForm.closed} onChange={(e) => setEditForm({ ...editForm, closed: e.target.checked })} />
                    </td>}
                    {isVisible('actions') && <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <a href="#s" onClick={(e) => { e.preventDefault(); handleEditSave(); }} style={{ ...styles.actionLink, color: 'green' }}>Save</a>
                      {' '}
                      <a href="#c" onClick={(e) => { e.preventDefault(); handleEditCancel(); }} style={styles.actionLink}>Cancel</a>
                    </td>}
                  </tr>
                );
              }
              const amount = fmt(t.amount != null ? t.amount : t.shares * t.price);
              const acctShort = t.account.includes('_') ? t.account.split('_').slice(1).join('_') : t.account;
              return (
                <tr key={t.id} style={{ backgroundColor: rowBg }}>
                  {isVisible('tdate') && <td style={styles.td}>{t.tdate}</td>}
                  {isVisible('account') && <td style={styles.td}>{acctShort}</td>}
                  {isVisible('ticker') && <td style={styles.td}><strong>{t.ticker}</strong></td>}
                  {isVisible('tranType') && <td style={{ ...styles.td, color: (t.tranType === TT_BUY || t.tranType === TT_XIN) ? 'green' : (t.tranType === TT_SELL || t.tranType === TT_XOUT) ? 'red' : '#333' }}>
                    {TRAN_TYPES[t.tranType] || t.tranType}
                  </td>}
                  {isVisible('shares') && <td style={{ ...styles.td, textAlign: 'right' }}>{t.shares}</td>}
                  {isVisible('price') && <td style={{ ...styles.td, textAlign: 'right' }}>{t.price.toFixed(3)}</td>}
                  {isVisible('amount') && <td style={{ ...styles.td, textAlign: 'right' }}>{amount}</td>}
                  {isVisible('commision') && <td style={{ ...styles.td, textAlign: 'right' }}>{t.commision?.toFixed(2) || '0.00'}</td>}
                  {isVisible('reasoning') && <td style={styles.td}>{t.reasoning || '-'}</td>}
                  {isVisible('closed') && <td style={{ ...styles.td, textAlign: 'center' }}>{t.closed ? 'Y' : ''}</td>}
                  {isVisible('actions') && <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <a href="#e" onClick={(e) => { e.preventDefault(); handleEdit(t); }} style={styles.actionLink}>Edit</a>
                    {' '}
                    <a href="#d" onClick={(e) => { e.preventDefault(); handleDelete(t.id); }} style={styles.actionLink}>Delete</a>
                  </td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
};

const CapReturnTab: React.FC = () => {
  const [portfolios, setPortfolios] = useState<CapPortfolio[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    transactionApi.capReturn()
      .then((res) => setPortfolios(res.data.portfolios))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const color = (val: number) => val >= 0 ? 'green' : 'red';

  if (loading) return <div style={{ textAlign: 'center', padding: '2em' }}>Loading Capital Return...</div>;

  return (
    <>
      {portfolios.map((p) => (
        <div key={p.portfolioName} style={{ marginBottom: '2em' }}>
          <h4 style={{ color: '#0069cc', margin: '0.5em 0', fontSize: '0.9em' }}>
            Portfolio: {p.portfolioName}
          </h4>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Company</th>
                <th style={styles.th}>Ticker</th>
                <th style={styles.th}>Shares</th>
                <th style={styles.th}>Cost</th>
                <th style={styles.th}>Dividend</th>
                <th style={styles.th}>Realized</th>
                <th style={styles.th}>Price</th>
                <th style={styles.th}>Unrealized</th>
                <th style={styles.th}>Total Value</th>
              </tr>
            </thead>
            <tbody>
              {p.items.map((item, idx) => {
                const rowBg = idx % 2 === 0 ? '#fff' : '#e6f2ff';
                return (
                  <tr key={item.ticker} style={{ backgroundColor: rowBg }}>
                    <td style={styles.td}>{item.companyName}</td>
                    <td style={styles.td}><strong>{item.exchange}:{item.ticker}</strong></td>
                    <td style={styles.tdR}>{fmtInt(item.shares)}</td>
                    <td style={styles.tdR}>{fmt(item.cost)}</td>
                    <td style={styles.tdR}>{item.dividend.toFixed(2)}</td>
                    <td style={{ ...styles.tdR, color: color(item.realizedGain) }}>{fmt(item.realizedGain)}</td>
                    <td style={styles.tdR}>{item.currentPrice.toFixed(2)}</td>
                    <td style={{ ...styles.tdR, color: color(item.unrealized) }}>{fmt(item.unrealized)}</td>
                    <td style={styles.tdR}>{fmt(item.totalValue)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: '#cce6ff', fontWeight: 'bold' }}>
                <td style={styles.td} colSpan={3}>Total ({p.items.length} positions)</td>
                <td style={styles.tdR}>{fmt(p.totalCost)}</td>
                <td style={styles.tdR}>{p.totalDividend.toFixed(2)}</td>
                <td style={{ ...styles.tdR, color: color(p.totalRealizedGain) }}>{fmt(p.totalRealizedGain)}</td>
                <td style={styles.tdR}></td>
                <td style={{ ...styles.tdR, color: color(p.totalUnrealized) }}>{fmt(p.totalUnrealized)}</td>
                <td style={styles.tdR}>{fmt(p.totalCost + p.totalUnrealized)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ))}
    </>
  );
};

/* ---------- Add New Transaction Tab ---------- */
const AddTransactionTab: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [reasons, setReasons] = useState<string[]>([]);
  const [form, setForm] = useState({ ...emptyForm, closed: false });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState('');

  useEffect(() => {
    accountApi.list().then(r => {
      setAccounts(r.data);
      const def = r.data.find((a: any) => a.account === 'JointPrimary');
      setForm(f => ({ ...f, account: def ? def.account : (r.data[0]?.account || '') }));
    }).catch(() => {});
    configApi.reasons().then(r => {
      const list = r.data.map((x: any) => x.reason || x.name || '');
      setReasons(list);
      if (list.length > 0) setForm(f => ({ ...f, reasoning: list[0] }));
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!form.ticker.trim()) { setResult('Ticker is required.'); return; }
    setSaving(true);
    setResult('');
    try {
      await transactionApi.create(form);
      setResult(`Transaction saved: ${TRAN_TYPES[form.tranType]} ${form.shares} ${form.ticker} @ ${form.price}`);
      setForm(f => ({ ...f, ticker: '', shares: 0, price: 0, commision: 3.95, notes: '' }));
    } catch (e: any) {
      setResult(e.response?.data?.detail || 'Failed to save transaction.');
    }
    setSaving(false);
  };

  return (
    <div style={{ maxWidth: '40em', margin: '0.5em auto' }}>
      <fieldset style={styles.fieldset}>
        <legend style={styles.legend}>New Transaction</legend>
        <ol style={styles.fieldList}>
          <li style={styles.field}>
            <label style={styles.formLabel}>Account:</label>
            <select value={form.account} onChange={e => setForm({ ...form, account: e.target.value })} style={styles.select}>
              {accounts.map(a => <option key={a.id} value={a.account}>{a.account}</option>)}
            </select>
          </li>
          <li style={styles.field}>
            <label style={styles.formLabel}>Ticker:</label>
            <input value={form.ticker} onChange={e => setForm({ ...form, ticker: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '10em' }} />
          </li>
          <li style={styles.field}>
            <label style={styles.formLabel}>Type:</label>
            <select value={form.tranType} onChange={e => setForm({ ...form, tranType: parseInt(e.target.value) })} style={styles.select}>
              {Object.entries(TRAN_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </li>
          <li style={styles.field}>
            <label style={styles.formLabel}>Date:</label>
            <input type="date" value={form.tdate} onChange={e => setForm({ ...form, tdate: e.target.value })} style={styles.input} />
          </li>
          <li style={styles.field}>
            <label style={styles.formLabel}>Shares:</label>
            <input type="number" value={form.shares} onChange={e => setForm({ ...form, shares: parseFloat(e.target.value) || 0 })} style={{ ...styles.input, width: '10em' }} />
          </li>
          <li style={styles.field}>
            <label style={styles.formLabel}>Price:</label>
            <input type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} style={{ ...styles.input, width: '10em' }} />
          </li>
          <li style={styles.field}>
            <label style={styles.formLabel}>Commission:</label>
            <input type="number" step="0.01" value={form.commision} onChange={e => setForm({ ...form, commision: parseFloat(e.target.value) || 0 })} style={{ ...styles.input, width: '10em' }} />
          </li>
          <li style={styles.field}>
            <label style={styles.formLabel}>Reasoning:</label>
            <select value={form.reasoning} onChange={e => setForm({ ...form, reasoning: e.target.value })} style={styles.select}>
              {reasons.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </li>
          <li style={styles.field}>
            <label style={styles.formLabel}>Notes:</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ ...styles.input, width: '20em', height: '5em' }} />
          </li>
          <li style={styles.field}>
            <label style={styles.formLabel}>Closed:</label>
            <select value={form.closed ? 'true' : 'false'} onChange={e => setForm({ ...form, closed: e.target.value === 'true' })} style={styles.select}>
              <option value="false">false</option>
              <option value="true">true</option>
            </select>
          </li>
        </ol>
        <fieldset style={{ border: 'none', textAlign: 'right', padding: '0.3em 1em 1em' }}>
          <input type="button" value={saving ? 'Saving...' : 'Save'} onClick={handleSave} disabled={saving} style={styles.button} />
        </fieldset>
      </fieldset>
      {result && (
        <div style={{
          fontSize: '0.85em', marginTop: '0.5em', padding: '6px 12px',
          backgroundColor: result.startsWith('Transaction saved') ? '#d4edda' : '#f8d7da',
          borderRadius: '4px', fontWeight: 'bold',
        }}>
          {result}
        </div>
      )}
    </div>
  );
};

/* ---------- Main Component ---------- */
const TransactionsPage: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('transactions');

  return (
    <div style={styles.container}>
      <div style={styles.tabs}>
        {TABS.map((t) => (
          <span
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{ ...styles.tabItem, ...(tab === t.key ? styles.activeTab : {}) }}
          >
            {t.label}
          </span>
        ))}
      </div>

      {tab === 'transactions' && <TransactionsTab showAll={false} />}
      {tab === 'allTransactions' && <TransactionsTab showAll={true} />}
      {tab === 'addTransaction' && <AddTransactionTab />}
      {tab === 'capReturn' && <CapReturnTab />}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '1em 0.5em', backgroundColor: '#fff', minHeight: 'calc(100vh - 6em)' },
  tabs: { display: 'flex', gap: '0', marginBottom: '1em', borderBottom: '2px solid #0d58e6' },
  tabItem: { padding: '6px 16px', cursor: 'pointer', fontSize: '0.85em', fontWeight: 'bold', backgroundColor: '#e6f2ff', border: '1px solid #0d58e6', borderBottom: 'none', marginRight: '2px' },
  activeTab: { backgroundColor: '#cce6ff', color: '#000' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5em 0', flexWrap: 'wrap' as const, gap: '0.5em' },
  filters: { display: 'flex', alignItems: 'center', fontSize: '0.85em', flexWrap: 'wrap' as const, gap: '4px' },
  formWrapper: { maxWidth: '40em', margin: '1em auto' },
  fieldset: { border: '1px solid #808080', backgroundColor: '#fff', padding: '0.8em 1em 0' },
  legend: { fontWeight: 'bold', marginLeft: '0.5em', padding: '0 0.2em' },
  errorMsg: { border: '1px dotted #700', color: '#a00', padding: '0.5em 1em', marginBottom: '0.5em' },
  fieldList: { listStyle: 'none', padding: '0.8em 1em 0', margin: 0 },
  field: { paddingBottom: '0.8em', display: 'flex', alignItems: 'center' },
  formLabel: { width: '9em', textAlign: 'right' as const, marginRight: '1em' },
  input: { padding: '2px 4px', fontFamily: 'Arial, sans-serif', fontSize: '1em' },
  select: { padding: '2px 4px', fontFamily: 'Arial, sans-serif', fontSize: '1em' },
  button: { padding: '4px 16px', backgroundColor: '#ccdcfc', color: '#000', border: '1px solid #808080', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '1em' },
  table: { width: '100%', marginTop: '0.5em', border: '2px solid #0d58e6', borderCollapse: 'collapse' as const },
  th: { border: '1px solid #0d58e6', padding: '4px 6px', backgroundColor: '#cce6ff', fontWeight: 'bold', fontSize: '0.8em', textAlign: 'center' as const },
  td: { border: '1px solid #0d58e6', padding: '3px 6px', fontSize: '0.8em' },
  tdR: { border: '1px solid #0d58e6', padding: '3px 6px', fontSize: '0.8em', textAlign: 'right' as const },
  actionLink: { color: '#0069cc', textDecoration: 'none', cursor: 'pointer', fontSize: '0.9em' },
};

export default TransactionsPage;
